// ============================================================
// /project/info 接口响应类型
// ============================================================

import type {ProjectId} from '@/types';

/** 漫剧类型 */
export const ComicType = {
    Dialogue: 1, // 对话型
    Narration: 2, // 旁白型
} as const;
export type ComicTypeValue = (typeof ComicType)[keyof typeof ComicType];

/** 画风类型 */
export const ComicStyleType = {
    Official: 1, // 官方
    Custom: 2, // 自定义
} as const;
export type ComicStyleTypeValue = (typeof ComicStyleType)[keyof typeof ComicStyleType];

/** 项目基本信息（/project/info → projectInfo） */
export interface ProjectInfo {
    projectId: ProjectId;
    projectName: string;
    projectDescription: string;
    projectCover: string;
    comicType: ComicTypeValue;
    comicRatio: string; // 画面比例
    comicStyleType: ComicStyleTypeValue;
    comicStyleId: number; // 画风ID
    styleName?: string; // 画风名称
    deliveryDate: string | null; // 交付日期，可空
    remainingDays: number; // 剩余天数（负数表示已逾期）
    totalDays: number | null; // 项目总天数（用于计算预警阈值）
    healthScore: number; // 健康度
    totalEpisodesCount: number; // 总剧本剧集（表：comic_script_episodes）
    overEpisodesCount: number; // 已完成剧本剧集（表：comic_script_episodes）
    totalShotCount: number; // 总分镜
    overShotCount: number; // 已完成分镜
    totalFinalcutCount: number; // 总成片数（表：comic_episodes）
    overFinalcutCount: number; // 已完成成片数（表：video_export_record）
    overAssetCount: number; // 已完成资产数
    totalAssetCount: number; // 资产总数
    maxBackCount: number;
    downloadAllowedRoles?: string[]; // 允许下载资产的角色代码列表
}

/** 剧本状态 */
export const ScriptStatus = {
    NotStarted: 0, // 未开始
    InProgress: 1, // 进行中
    Completed: 2, // 已完成
} as const;
export type ScriptStatusValue = (typeof ScriptStatus)[keyof typeof ScriptStatus];

/** 进度信息（/project/info → progressInfo） */
export interface ProgressInfo {
    scriptStatus: ScriptStatusValue;
    scriptEpisodeCount: number; // 剧本总集数
    assetTotalCount: number; // 资产总数
}

/** 积分信息（/project/info → pointInfo） */
export interface PointInfo {
    totalBudget: number; // 总积分预算
    usedPoints: number; // 已使用积分
    remainingPoints: number; // 剩余积分
    accountPoints: number; // 账户积分（当前登录人）
}

/** 项目成员（/project/info → members[]） */
export interface ProjectMember {
    userId: number; // 用户ID
    userName: string; // 用户名
    avatar: string; // 头像
    uKey: string; // 加密uid（每次请求不同，仅作为提交后端的载荷）
    mKey: string; // 稳定的成员唯一标识，跨请求一致，用于前端身份匹配
    roleCode: string[]; // 角色代码列表
    roleName?: string[]; // 角色名称列表
}

/** /project/info 完整响应 data */
export interface ProjectInfoData {
    projectInfo: ProjectInfo;
    progressInfo: ProgressInfo;
    pointInfo: PointInfo;
    members: ProjectMember[];
}

export interface ScriptInfo {
    scriptId: number;
    status: number; // 剧本状态
    rawScriptSummary?: string; // 剧本概览，可空
    scriptDetail?: string; // 剧本详情，可空
}

// ============================================================
// /asset/info 接口响应类型（包含剧本+资产信息）
// ============================================================

/** 角色条目 */
export interface CharacterItem {
    characterId: number;
    characterName: string;
    characterImage: string;
}

/** 角色信息（/asset/info → characterInfo） */
export interface CharacterInfo {
    total: number;
    characterList?: CharacterItem[];
}

/** 场景条目 */
export interface SceneItem {
    sceneId: number;
    sceneName: string;
    sceneImage: string;
}

/** 场景信息（/asset/info → sceneInfo） */
export interface SceneInfo {
    total: number;
    sceneList?: SceneItem[];
}

/** 道具条目 */
export interface PropItem {
    propId: number;
    propName: string;
    propImage: string;
}

/** 道具信息（/asset/info → propInfo） */
export interface PropInfo {
    total: number;
    propList?: PropItem[];
}

/** 资产解析状态 */
export enum AssetStatus {
    Failed = -1, // 解析失败
    Initial = 0, // 初始态（未开始）
    Parsing = 1, // 提取中
    Done = 2, // 提取成功
}

/** 资产提取轮询状态（/asset/extract/status/polling） */
export enum AssetExtractStatus {
    Failed = -1, // 失败
    Initial = 0, // 初始态
    StartExtracting = 1, // 提取中
    Success = 2, // 提取成功
}

/** /asset/info 完整响应 data（新版：合并剧本和资产信息） */
export interface AssetInfoData {
    scriptInfo: ScriptInfo; // 剧本信息
    assetStatus: AssetStatus; // 资产状态
    taskId: string; // 资产提取任务 ID
    characterInfo?: CharacterInfo; // 角色信息
    sceneInfo?: SceneInfo; // 场景信息
    propInfo?: PropInfo; // 道具信息
}

// ============================================================
// /episodes/list 接口响应类型
// ============================================================

export enum AuditStatus {
    Initial = 1, // 初始态
    Pass = 2, // 审核通过
    Done = 3, // 驳回
}

/** 剧集状态 */
export const EpisodeStatus = {
    /** 待创作（没有审批通过的分镜视频） */
    PendingCreation: 1,
    /** 分镜制作中（有审批通过的分镜视频） */
    StoryboardInProgress: 2,
    /** 待剪辑未导出（分镜视频都审核完成，但没有成片记录） */
    PendingEditNoExport: 3,
    /** 待剪辑已导出（分镜视频都审核完成，有成片记录但没有审批通过首选的） */
    PendingEditExported: 4,
    /** 已完成（分镜都审核完成，成片有首选） */
    Completed: 5,
} as const;
export type EpisodeStatusValue = (typeof EpisodeStatus)[keyof typeof EpisodeStatus];

/** 剧集条目（/episodes/list → episodesList[]） */
export interface EpisodeItem {
    episodeId: number;
    title?: string;
    coverUrl?: string;
    videoUrl?: string;
    totalShotCount?: number; // 总分镜数
    overShotCount?: number; // 已完成分镜数
    status: EpisodeStatusValue; // 集状态
}

/** /episodes/list 完整响应 data */
export interface EpisodeListData {
    total: number;
    episodesList: EpisodeItem[];
    agentId: string;
    recycleRetentionDays?: number; // 回收箱保留天数，后端 GCP 下发
}

/** 回收站条目（/episode/deleted/list → list[]） */
export interface DeletedEpisodeItem {
    episodeId: number;
    title: string;
    coverUrl: string;
    deleteTime: number; // 毫秒时间戳，移入回收站的时间
    expireTime: number; // deleteTime + 保留天数
    remainMs?: number; // 剩余保留毫秒数，服务端按自身时钟下发
}

/** /episode/deleted/list 完整响应 data */
export interface DeletedEpisodeListData {
    // keyword 为空时等于回收站实际条数，非空时是命中数，搜索态下不能用它刷新 Tab 计数
    total: number;
    list: DeletedEpisodeItem[];
    recycleRetentionDays?: number; // 回收箱保留天数，后端 GCP 下发
}

/** 批量操作结果，invalidIds 为已被他人恢复 / 已超期 / 已永久删除的项 */
export interface EpisodeBatchResult {
    // 按可缺省声明：接口异常时 data 可能是空对象，读取方一律走兜底
    successIds?: number[];
    invalidIds?: number[];
    successCount?: number; // 清空回收站只回条数，successIds 为空数组
}

/** 版本选择状态 */
export const SelectionStatus = {
    Unselected: 0, // 未选择
    Preferred: 1, // 首选
    Alternate: 2, // 备选
    Eliminated: 3, // 淘汰
} as const;
export type SelectionStatusValue = (typeof SelectionStatus)[keyof typeof SelectionStatus];

/** 视频渲染状态 */
export const VideoRenderStatus = {
    Initial: 0, // 初始态
    Rendering: 1, // 渲染中
    Success: 2, // 渲染成功
    Failed: 3, // 渲染失败
} as const;
export type VideoRenderStatusValue = (typeof VideoRenderStatus)[keyof typeof VideoRenderStatus];

/** 视频版本信息（/video/episode/list → list[]） */
export interface EpisodeVersion {
    videoId: string;
    versionName: string;
    createAt: number; // 秒级时间戳
    videoUrl: string;
    coverUrl: string;
    directorComment: string;
    selectionStatus: SelectionStatusValue;
    status: VideoRenderStatusValue;
}

/** /video/episode/list 完整响应 data */
export interface EpisodeVersionListData {
    list: EpisodeVersion[];
}

// ============================================================
// 前端聚合类型
// ============================================================

/**
 * 项目概览聚合数据（由 useProjectOverview 组装）
 * - projectInfo / progressInfo / pointInfo / members 来自 /project/info
 * - script 来自 /script/info
 * - assets 来自 /asset/info
 */
export interface ProjectOverviewData {
    projectInfo?: ProjectInfo;
    progressInfo?: ProgressInfo;
    pointInfo?: PointInfo;
    members?: ProjectMember[];
    script?: ScriptInfo;
    assets?: AssetInfoData;
}

// ============================================================
// /project/update 请求参数类型
// ============================================================

/** 项目设置（对应 /project/update 请求参数） */
export interface ProjectSettings {
    projectId?: ProjectId;
    projectName?: string;
    projectDescription?: string;
    projectCover?: string;
    comicType?: ComicTypeValue;
    comicRatio?: string;
    comicStyleType?: ComicStyleTypeValue;
    comicStyleId?: number;
    chargePoint?: number;
    maxBackCount?: number; // 最大备选提交数量
    deliveryDate?: string; // 交付日期 yyyy-MM-dd
    downloadAllowedRoles?: string[]; // 允许下载资产的角色代码列表
}

/** 成员角色条目（/proxy-aicomic/member/list 响应） */
export interface MemberRoleItem {
    roleCode: string;
    roleName: string;
    description: string;
    roleLevel: number;
    hover: string;
}
